import { DatePicker, Select } from '@ph-react-ui/core';
import type { SelectOption, SimpleDate } from '@ph-react-ui/core';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { PathMatch } from 'react-router-dom';
import { useGetSchoolYearBaseSettings } from '../../../hooks/settings/useGetSchoolYearBaseSettings';
import { useGetStudentsForParent } from '../../../hooks/users/parents/useGetStudentsForParent';
import { Scholarship } from '../../../models/users/students/scholarship-type';
import { mapStringToSimpleDate } from '../../../services/mapStringToSimpleDate';
import { PaymentMethodEnum } from '../../../utils/enums/payment-method.enum';
import { PaymentStatus } from '../../../utils/enums/payment-status.enum';
import { SearchInput } from './SearchByNameInput';

interface TaxesFiltersProps {
  pathMatch: PathMatch | null;
  showPaymentPeriod?: boolean;
  hideFromToDatePickers?: boolean;
  showChildDropdown?: boolean;
  searchByName?: boolean;
  filterUsingRestaurantOnly?: boolean;
}

export const TaxesFilters = ({
  pathMatch,
  showPaymentPeriod = false,
  hideFromToDatePickers = false,
  showChildDropdown = false,
  searchByName = false,
  filterUsingRestaurantOnly = false,
}: TaxesFiltersProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromDateParam = searchParams.get('fromDate');
  const toDateParam = searchParams.get('toDate');
  const isPaid = searchParams.get('isPaid');
  const paymentMethodType = searchParams.get('paymentMethodType');
  const scholarshipType = searchParams.get('scholarshipType');
  const studentParamKey = searchByName ? 'name' : 'studentId';
  const studentParam = searchParams.get(studentParamKey);

  const { data: schoolYearData } = useGetSchoolYearBaseSettings();
  const { data: students } = useGetStudentsForParent({
    enabled: showChildDropdown,
    isStudyingInCurrentYear: true,
  });

  const [studentOptions, setStudentOptions] = useState<SelectOption<string>[]>(
    []
  );

  useEffect(() => {
    if (students && showChildDropdown) {
      const filteredStudents = filterUsingRestaurantOnly
        ? students.filter((student) => student.isUsingRestaurant)
        : students;

      const options = filteredStudents.map((student) => ({
        label: student.name,
        value: searchByName ? student.name : student.id,
      }));
      setStudentOptions(options);
    }
  }, [students, showChildDropdown, searchByName, filterUsingRestaurantOnly]);

  const statusOptions = useMemo<SelectOption<number | string>[]>(
    () => [
      { label: t('TAXES.LABELS.ALL'), value: '' },
      { label: t('TAXES.LABELS.PAID'), value: PaymentStatus.Paid },
      { label: t('TAXES.LABELS.NOT_PAID'), value: PaymentStatus.NotPaid },
    ],
    [t]
  );

  const paymentMethodOptions = useMemo<SelectOption<number | string>[]>(
    () => [
      { label: t('TAXES.LABELS.ALL'), value: '' },
      { label: t('TAXES.LABELS.CARD'), value: PaymentMethodEnum.CARD },
      { label: t('TAXES.LABELS.TRANSFER'), value: PaymentMethodEnum.TRANSFER },
    ],
    [t]
  );

  const scholarshipTypeOptions = useMemo<SelectOption<number | string>[]>(
    () => [
      { label: t('TAXES.LABELS.ALL'), value: '' },
      {
        label: t('TAXES.LABELS.NO_SCHOLARSHIP'),
        value: Scholarship.NO_SCHOLARSHIP,
      },
      {
        label: t('TAXES.LABELS.FULL_SCHOLARSHIP'),
        value: Scholarship.FULL_SCHOLARSHIP,
      },
      {
        label: t('TAXES.LABELS.PARTIAL_SCHOLARSHIP'),
        value: Scholarship.PARTIAL_SCHOLARSHIP,
      },
    ],
    [t]
  );

  const onSetToDate = (value: SimpleDate | null) => {
    if (value) {
      const date = new Date(value.year, value.month, value.date);
      searchParams.set('toDate', format(date, 'yyyy-MM-dd'));
      searchParams.set('page', '1');
    } else {
      searchParams.delete('toDate');
    }
    setSearchParams(searchParams);
  };

  const onSetFromDate = (value: SimpleDate | null) => {
    if (value) {
      const date = new Date(value.year, value.month, value.date);
      searchParams.set('fromDate', format(date, 'yyyy-MM-dd'));
      searchParams.set('page', '1');
    } else {
      searchParams.delete('fromDate');
    }
    setSearchParams(searchParams);
  };

  const handleDropdownChange = (
    newVal: null | string | number | (string | number)[],
    searchParamKey: string,
    evalFunc?: (newVal: null | string | number | (string | number)[]) => string
  ) => {
    if (newVal === null) {
      return;
    }
    const value: string = evalFunc ? evalFunc(newVal) : (newVal as string);
    setSearchParamByKey(searchParamKey, value);
  };

  const setSearchParamByKey = (key: string, value: string) => {
    searchParams.set(key, value);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const schoolYearOptions = useMemo<SelectOption<number | string>[]>(() => {
    const currentYear = new Date().getFullYear();
    return [
      { label: t('TAXES.LABELS.ALL'), value: '' },
      ...Array.from({ length: currentYear - 2024 + 11 }, (_, i) => {
        const year = 2024 + i;
        return {
          label: `${year} - ${year + 1}`,
          value: year.toString(),
        };
      }),
    ];
  }, [t]);

  const startYearParam = searchParams.get('startYear');

  const onSetSchoolYear = (
    value: string | number | (string | number)[] | null
  ) => {
    if (value && value !== '') {
      searchParams.set('startYear', value.toString());
      searchParams.set('page', '1');
    } else {
      searchParams.delete('startYear');
    }
    setSearchParams(searchParams);
  };

  const onSelectStudent = (
    value: string | number | (string | number)[] | null
  ) => {
    const paramKey = searchByName ? 'name' : 'studentId';

    if (value) {
      searchParams.set(paramKey, value.toString());
      searchParams.set('page', '1');
    } else {
      searchParams.delete(paramKey);
    }
    setSearchParams(searchParams);
  };

  return (
    <>
      {showChildDropdown ? (
        <div className="taxes__food__filter__select">
          <Select
            placeholder={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            compact
            options={studentOptions}
            onChange={onSelectStudent}
            value={studentParam || ''}
            label={t('TAXES.LABELS.CHOOSE_STUDENT') ?? ''}
            clearable
            searchable
          />
        </div>
      ) : (
        <SearchInput
          pathMatch={pathMatch}
          placeholder={t('TAXES.FILTERS.SEARCH_BY_NAME')}
        />
      )}
      {!hideFromToDatePickers && (
        <div className="taxes__food__filter__dates">
          <DatePicker
            value={mapStringToSimpleDate(fromDateParam)}
            compact
            onChange={onSetFromDate}
            placeholder={t('TAXES.LABELS.FROM_DATE') ?? ''}
            maxDate={schoolYearData?.endOfSchoolYear}
            label={t('TAXES.LABELS.FROM_DATE') ?? ''}
          />
          <DatePicker
            value={mapStringToSimpleDate(toDateParam)}
            compact
            onChange={onSetToDate}
            placeholder={t('TAXES.LABELS.TO_DATE') ?? ''}
            maxDate={schoolYearData?.endOfSchoolYear}
            label={t('TAXES.LABELS.TO_DATE') ?? ''}
          />
        </div>
      )}

      <div className="taxes__food__filter__select">
        <Select
          placeholder={t('TAXES.FILTERS.STATUS') ?? ''}
          compact
          options={statusOptions}
          onChange={(status) =>
            handleDropdownChange(status, 'isPaid', (status) =>
              status === PaymentStatus.Paid
                ? 'true'
                : status === PaymentStatus.NotPaid
                ? 'false'
                : ''
            )
          }
          value={
            isPaid === 'true'
              ? PaymentStatus.Paid
              : isPaid === 'false'
              ? PaymentStatus.NotPaid
              : ''
          }
          label={t('TAXES.FILTERS.STATUS') ?? ''}
        />
      </div>
      <div className="taxes__food__filter__select">
        <Select
          placeholder={t('TAXES.FILTERS.PAYMENT_METHOD') ?? ''}
          compact
          options={paymentMethodOptions}
          onChange={(method) => {
            handleDropdownChange(method, 'paymentMethodType');
            //the backend returns non paid payments when the payment method is Transfer, so we need to set isPaid to true
            setSearchParamByKey('isPaid', 'true');
          }}
          value={paymentMethodType ? +paymentMethodType : ''}
          label={t('TAXES.FILTERS.PAYMENT_METHOD') ?? ''}
        />
      </div>
      <div className="taxes__food__filter__select">
        <Select
          placeholder={t('TAXES.FILTERS.EDUCATION_TYPE') ?? ''}
          compact
          options={scholarshipTypeOptions}
          onChange={(status) => handleDropdownChange(status, 'scholarshipType')}
          value={scholarshipType ? +scholarshipType : ''}
          label={t('TAXES.FILTERS.EDUCATION_TYPE') ?? ''}
        />
      </div>
      {showPaymentPeriod && (
        <div className="taxes__food__filter__select">
          <Select
            placeholder={t('TAXES.FILTERS.SCHOOL_YEAR') ?? ''}
            compact
            options={schoolYearOptions}
            onChange={onSetSchoolYear}
            value={startYearParam || ''}
            label={t('TAXES.FILTERS.SCHOOL_YEAR') ?? ''}
          />
        </div>
      )}
    </>
  );
};
